import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';
import { IconButton } from './IconButton';
import { Panel } from './Panel';
import { computePopoverPlacement, type PopoverBounds, type Rect } from '../../utils/popoverPlacement';
import { useEscapeKey, useOutsidePointerDown } from './overlayHooks';

const CARD_WIDTH = 268;
const CARD_HEIGHT_ESTIMATE = 200;

export interface PopoverHandle {
    close: () => void;
}

export interface PopoverProps {
    open: boolean;
    anchorRect: Rect;
    bounds: PopoverBounds;
    onDismiss: () => void;
    title?: ReactNode;
    children?: ReactNode;
    className?: string;
    /** Test id set on the portalled card, so a spec can target this popover rather than any element sharing its animation class. */
    'data-testid'?: string;
}

export const Popover = forwardRef<PopoverHandle, PopoverProps>(function Popover(
    { open, anchorRect, bounds, onDismiss, title, children, className, 'data-testid': testId },
    ref,
) {
    const cardRef = useRef<HTMLDivElement>(null);
    const [cardHeight, setCardHeight] = useState(CARD_HEIGHT_ESTIMATE);

    useImperativeHandle(ref, () => ({ close: onDismiss }), [onDismiss]);

    useEscapeKey(open, onDismiss);
    useOutsidePointerDown(open, cardRef, onDismiss);

    useEffect(() => {
        if (open && cardRef.current) {
            setCardHeight(cardRef.current.offsetHeight);
        }
    }, [open, children]);

    if (!open) return null;

    const { left, top, transform } = computePopoverPlacement(
        anchorRect,
        { width: CARD_WIDTH, height: cardHeight },
        bounds,
    );

    return createPortal(
        <Panel
            ref={cardRef}
            variant="glass"
            padding="none"
            className={cn('fixed z-50 w-[268px] max-w-[calc(100vw-16px)] p-3.5 animate-pop-in', className)}
            data-testid={testId}
            style={{ left, top, transform }}
            // React bubbles a portal's synthetic events through the REACT
            // tree, not the DOM tree — so without this, a pointerdown inside
            // the card (rendered via `createPortal` to `document.body`) still
            // reaches whatever draggable/hit-testing ancestor this popover is
            // logically nested under (e.g. DS-17's `PlanetSurface`), which
            // then treats it as a tap on the page behind the card and closes
            // the popover before its own click handler ever runs.
            onPointerDown={(event) => event.stopPropagation()}
        >
            <IconButton
                aria-label="Close"
                size="sm"
                variant="ghost"
                className="absolute right-2 top-2"
                onClick={onDismiss}
            >
                <span aria-hidden="true">&times;</span>
            </IconButton>
            {title && <div className="text-title text-content-primary mb-2 pr-6">{title}</div>}
            {children}
        </Panel>,
        document.body,
    );
});

export default Popover;
